<template>
    <div class="min-h-screen bg-gray-50/50">
        <div class="mx-auto max-w-7xl space-y-5 px-4 py-8 sm:px-6 lg:px-8">
            <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 class="text-xl font-semibold text-gray-800">
                        {{ t("vue.kiosk_ai_spend") }}
                    </h1>
                    <p class="text-sm text-gray-500">
                        {{ range.from }} → {{ range.to }}
                    </p>
                </div>

                <div
                    class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5"
                >
                    <inertia-link
                        v-for="p in periods"
                        :key="p"
                        :href="route('kiosk.ai-spend.index', { period: p })"
                        preserve-scroll
                        class="rounded-md px-3 py-1.5 text-sm font-medium capitalize transition"
                        :class="
                            p === period
                                ? 'bg-gray-800 text-white'
                                : 'text-gray-500 hover:bg-gray-50'
                        "
                    >
                        {{ t(`vue.kiosk_ai_spend_period_${p}`) }}
                    </inertia-link>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div class="rounded-xl border border-gray-200 bg-white p-5">
                    <p
                        class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_total") }}
                    </p>
                    <p class="mt-1 text-2xl font-semibold text-gray-800">
                        {{ formatUsd(spend.total_usd) }}
                    </p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-white p-5">
                    <p
                        class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_priced_messages") }}
                    </p>
                    <p class="mt-1 text-2xl font-semibold text-gray-800">
                        {{
                            new Intl.NumberFormat().format(
                                spend.priced_message_count,
                            )
                        }}
                    </p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-white p-5">
                    <p
                        class="text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_all_messages") }}
                    </p>
                    <p class="mt-1 text-2xl font-semibold text-gray-800">
                        {{
                            new Intl.NumberFormat().format(spend.message_count)
                        }}
                    </p>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div
                    class="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                    <h2
                        class="border-b border-gray-100 px-5 py-3 text-sm font-semibold text-gray-700"
                    >
                        {{ t("vue.kiosk_ai_spend_by_team") }}
                    </h2>
                    <p
                        v-if="spend.by_team.length === 0"
                        class="px-5 py-4 text-sm text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_empty") }}
                    </p>
                    <table v-else class="w-full text-sm">
                        <tbody>
                            <tr
                                v-for="team in spend.by_team"
                                :key="team.team_id ?? 'unknown'"
                                class="border-b border-gray-50 last:border-0"
                            >
                                <td class="px-5 py-2 text-gray-700">
                                    {{ team.team_name ?? `#${team.team_id}` }}
                                </td>
                                <td class="px-5 py-2 text-right text-gray-400">
                                    {{
                                        new Intl.NumberFormat().format(
                                            team.message_count,
                                        )
                                    }}
                                </td>
                                <td
                                    class="px-5 py-2 text-right font-medium text-gray-800"
                                >
                                    {{ formatUsd(team.total_usd) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div
                    class="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                    <h2
                        class="border-b border-gray-100 px-5 py-3 text-sm font-semibold text-gray-700"
                    >
                        {{ t("vue.kiosk_ai_spend_by_model") }}
                    </h2>
                    <p
                        v-if="spend.by_model.length === 0"
                        class="px-5 py-4 text-sm text-gray-400"
                    >
                        {{ t("vue.kiosk_ai_spend_empty") }}
                    </p>
                    <table v-else class="w-full text-sm">
                        <tbody>
                            <tr
                                v-for="model in spend.by_model"
                                :key="model.model"
                                class="border-b border-gray-50 last:border-0"
                            >
                                <td
                                    class="px-5 py-2 font-mono text-xs text-gray-700"
                                >
                                    {{ model.model }}
                                </td>
                                <td class="px-5 py-2 text-right text-gray-400">
                                    {{
                                        new Intl.NumberFormat().format(
                                            model.message_count,
                                        )
                                    }}
                                </td>
                                <td
                                    class="px-5 py-2 text-right font-medium text-gray-800"
                                >
                                    {{ formatUsd(model.total_usd) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";

interface ITeamSpend {
    team_id: number | null;
    team_name: string | null;
    total_usd: number;
    message_count: number;
}

interface IModelSpend {
    model: string;
    total_usd: number;
    message_count: number;
}

defineProps<{
    period: string;
    periods: string[];
    range: { from: string; to: string };
    spend: {
        total_usd: number;
        message_count: number;
        priced_message_count: number;
        by_team: ITeamSpend[];
        by_model: IModelSpend[];
    };
}>();

const { t } = useTranslations();

const formatUsd = (value: number): string =>
    `$${value.toFixed(value < 0.01 && value > 0 ? 4 : 2)}`;
</script>
